import { Descriptions, Popover } from 'antd';
import { ShipiumTag } from '../../components/shared/ShipiumTag';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { palette } from '@shipium-corp/shipium-ui-theme';
import type { SimulationSetupData, SellRateCarrierSelection, SimulationType } from '../../store/simulation/types';
import type { SimulationStep } from './SimulationStepMap';

export interface DescriptionItemWithPopoverOptions {
  itemLabel: string;
  itemValue: string | React.ReactNode | React.ReactNode[];
  popOverTitle: string | React.ReactNode | React.ReactNode[];
  popOverContent: string | React.ReactNode | React.ReactNode[];
}

export const descriptionItemWithPopover = ({
  itemLabel,
  itemValue,
  popOverTitle,
  popOverContent,
}: DescriptionItemWithPopoverOptions) => {
  const popover = <div>{popOverContent}</div>;

  return (
    <Descriptions.Item label={itemLabel}>
      <Popover title={popOverTitle} content={popover}>
        <ShipiumTag color={'Coral'} dark={true}>
          {itemValue}
        </ShipiumTag>
      </Popover>
    </Descriptions.Item>
  );
};

export const safeDateFormat = (date: string | undefined) => {
  if (!date) {
    return '';
  }
  return date.split('T')[0];
};

export const getColor = (val: number, invertColors = false) => {
  if (val === 0) return 'inherit';
  return val > 0 === invertColors ? palette.Coral.normal : palette.Teal.normal;
};

export const renderCaret = (val: number, invertColors?: boolean) => {
  const color = getColor(val, invertColors);
  if (val === 0) return null;

  return val > 0 ? <CaretUpOutlined style={{ color }} /> : <CaretDownOutlined style={{ color }} />;
};

export const shortenLabel = (rawLabel: string, length = 20) => {
  if (!rawLabel) {
    return '';
  }
  const label = rawLabel.split(',')[0];
  const returnParts = [];
  if (label.length <= length) {
    return label;
  }

  const parts = label.split(' ');
  for (let i = 0; i < parts.length; i++) {
    returnParts.push(parts[i]);
    if (returnParts.join(' ').length > length) {
      return returnParts.join(' ');
    }
  }
  return parts.join(' ');
};

// we don't want to show too many steps to the user
// if the workflow only has 4 then we show them as is
// otherwise we want to pad the front and/or back with "And n more" and show 3 steps
export const getStepsToShow = (steps: SimulationStep[], activeNavigationStep: number) => {
  const numberOfSteps = steps.length;

  // total number of steps is greater than 4 then we are hiding either
  // the number of steps we have completed (activeNav is 0 indexed so we can use that as our count)
  // or the total number of steps - 3 as we will stop hiding additional steps as we reach the end of the WF

  const hiddenCompletedSteps = numberOfSteps <= 4 ? 0 : Math.min(activeNavigationStep, steps.length - 3);
  // similar idea but for the "And N more" that appears at the end
  const hiddenFutureSteps = numberOfSteps <= 4 ? 0 : numberOfSteps - activeNavigationStep - 3;
  return [
    ...(hiddenCompletedSteps > 0 ? [{ title: `And ${hiddenCompletedSteps} more` }] : []),
    ...steps
      // if the workflow is <4 steps show 4 workflow steps
      // otherwise show 3 because we know we are going to have "And N mores added" on at least one side
      .slice(hiddenCompletedSteps, numberOfSteps <= 4 ? hiddenCompletedSteps + 4 : hiddenCompletedSteps + 3)
      .map((itm) => itm.stepItem),
    ...(hiddenFutureSteps > 0 ? [{ title: `And ${hiddenFutureSteps} more` }] : []),
  ];
};

export const getLatestCompletedStep = (steps: SimulationStep[], setupData: SimulationSetupData): number => {
  // find the the latest step you can navigate forward to based on WF state
  return (
    steps?.reduce((prev: number, step: SimulationStep, idx: number) => (setupData?.[step.stepKey] ? idx : prev), -1) ??
    -1
  );
};

// because I am only showing four steps but there are more in the workflow
// we have a calculated observed step which represent what you
// will in the nav steps based on where you are in the workflow
export const getObservedActiveStep = (activeNavigationStep: number, numberOfSteps: number) => {
  if (activeNavigationStep === 0) {
    return 0;
  }
  if (numberOfSteps - activeNavigationStep >= 3) {
    return 1;
  }
  return activeNavigationStep - numberOfSteps + 4;
};
export const getClickedStep = (
  clickedNumber: number,
  numberOfSteps: number,
  activeNavigationStep: number,
  latestCompletedStep: number,
) => {
  const observedStep = getObservedActiveStep(activeNavigationStep, numberOfSteps);
  // if you are clicking on the first step whan it shows
  // "and N more" just takes you back one step
  if (clickedNumber === 0 && numberOfSteps > 4) {
    return activeNavigationStep - 1;
  }
  // if you are clicking on a step behind your current step
  // takes the difference between the current step and the desired
  // one and subract that from the navigation
  if (clickedNumber < observedStep) {
    return activeNavigationStep - (observedStep - clickedNumber);
  }
  // do not navigate to a step they have not reached
  if (activeNavigationStep + clickedNumber > latestCompletedStep + 1) {
    return activeNavigationStep;
  }
  // if you are clicking on a step ahead of your current step
  // takes the difference between the current step and the desired
  // one and add that to the navigation step
  return activeNavigationStep + (clickedNumber - observedStep);
};

// Must keep in sync with
// https://gitlab.com/shipium-corp/engineering/shiptime-service/-/blob/master/service/src/main/java/com/shipium/shiptime/estimate/TimeInTransitKeyType.java
export const timeInTransitDataTypeMap: Record<string, string> = {
  no_desired_delivery_date: 'No Desired Delivery Date',
  partner_provided_postal: 'Partner Provided Postal',
  partner_provided_zone: 'Partner Provided Zone',
  shipium_calculated_postal: 'Shipium Calculated Postal',
  shipium_ml_model: 'Shipium ML Model',
  shipium_cached_ml_model: 'Shipium Cached ML Model',
  carrier_default_postal: 'Carrier Default Postal',
  carrier_default_single_origin_postal: 'Carrier Default Single Origin Postal',
  unknown: 'Unknown',
};

export const partnerTimeInTransitDataTypeMap: Record<string, string> = {
  no_desired_delivery_date: 'No Desired Delivery Date',
  partner_provided_postal: 'Partner Provided',
  partner_provided_zone: 'Partner Provided',
  shipium_calculated_postal: 'Shipium ML Model',
  shipium_ml_model: 'Shipium ML Model',
  shipium_cached_ml_model: 'Shipium ML Model',
  carrier_default_postal: 'Carrier Provided',
  carrier_default_single_origin_postal: 'Carrier Provided',
  unknown: 'Unknown',
};

export const carrierColorMap = new Map([
  ['airterra', 'FE9014'],
  ['apc', '4B2682'],
  ['asendia', '005D68'],
  ['lasership', 'ED1C24'],
  ['pandion', 'C63073'],
  ['usps', '333366'],
  ['ups', '704A35'],
  ['upsmi', '097C7C'],
  ['fedex', 'FF6101'],
  ['ontrac', 'FFC300'],
  ['cdl', '692659'],
  ['xdelivery', '3BB800'],
  ['veho', 'E74931'],
  ['dhl', 'FFCB00'],
]);

export const sumSellRateFields = (
  cs1?: SellRateCarrierSelection,
  cs2?: SellRateCarrierSelection,
  includeCarrierSurcharges = true,
) => {
  const {
    simulationTotalLabelCost = 0,
    simulationTotalSellRateCost = 0,
    simulationTotalBaseSellRateCost = 0,
    simulationCount = 0,
  } = cs1 ?? {};
  const {
    simulationTotalLabelCost: simulationTotalLabelCost2 = 0,
    simulationTotalSellRateCost: simulationTotalSellRateCost2 = 0,
    simulationTotalBaseSellRateCost: simulationTotalBaseSellRateCost2 = 0,
    simulationCount: simulationCount2 = 0,
  } = cs2 ?? {};
  return {
    simulationTotalLabelCost: simulationTotalLabelCost + simulationTotalLabelCost2,
    simulationTotalSellRateCost: includeCarrierSurcharges
      ? simulationTotalSellRateCost + simulationTotalSellRateCost2
      : simulationTotalBaseSellRateCost2 + simulationTotalBaseSellRateCost,
    simulationCount: simulationCount2 + simulationCount,
  };
};

export const SimulationTypeToDisplay: Record<SimulationType, string> = {
  advanced: 'Advanced Simulation',
  flat_rate_update: 'Fixed Carrier Rate Change',
  add_carrier_method: 'Add Carrier and Method',
  add_origin: 'Add New Origin',
  new_rate_sheet: 'Add New Rate Sheet',
  sell_rate_simulation: 'Sell Rate Margin Simulation',
};
